import isEqual from 'lodash/isEqual';
import {
  SearchCandidate,
  StructureLeg,
} from 'gen/stream-api/requests/types.gen';
import { MoviefoneRiskTolerance } from 'states/moviefone';
import {
  LOW_RISK_MAX,
  MEDIUM_RISK_MAX,
  RiskTier,
  tierFromRiskScore,
} from 'util/moviefoneFormat';

export interface TradeCard {
  /** Null when the SearchCandidate carries no risk_score to classify it by. */
  tier: RiskTier | null;
  searchCandidate: SearchCandidate;
}

/** Pick up to one SearchCandidate per risk tier, preferring the highest-scoring in each. */
function pickByRiskTier(strategies: SearchCandidate[]): TradeCard[] {
  const byTier = new Map<RiskTier, SearchCandidate>();
  for (const s of strategies) {
    if (s.risk_score == null) {
      continue;
    }
    const tier = tierFromRiskScore(s.risk_score.total);
    const existing = byTier.get(tier);
    if (existing == null || s.final_score! > existing.final_score!) {
      byTier.set(tier, s);
    }
  }
  const ordered: RiskTier[] = ['low', 'medium', 'high'];
  return ordered
    .filter((tier) => byTier.has(tier))
    .map((tier) => ({ tier, searchCandidate: byTier.get(tier)! }));
}

/**
 * Pick trade cards to show, preferring real risk-tier classification.
 * Falls back to the top-scoring strategies (untiered) when the response
 * doesn't carry risk_score, so real recommendations still surface.
 */
export function pickTradeCards(
  unfileredStrategies: SearchCandidate[],
  riskTolerance: MoviefoneRiskTolerance,
  min_pnl_ratio: number,
  max_pnl_ratio: number,
): TradeCard[] {
  const strategies = unfileredStrategies
    .filter((s) => {
      if (s.max_risk == null) {
        return false;
      }
      const maxPnl = (s.max_profit ?? Infinity) / s.max_risk;
      return (
        (riskTolerance == 'high' ||
          (s.risk_score?.total != null &&
            s.risk_score?.total <
              (riskTolerance == 'low' ? LOW_RISK_MAX : MEDIUM_RISK_MAX))) &&
        min_pnl_ratio <= maxPnl &&
        maxPnl <= (max_pnl_ratio ?? Infinity)
      );
    })
    .sort(
      (a, b) =>
        (a.risk_score?.total ?? Infinity) - (b.risk_score?.total ?? Infinity),
    );

  if (strategies.length == 0) {
    return [];
  }

  if (riskTolerance != 'high') {
    const maxIdx = strategies.length - 1;
    const lo = Math.floor(maxIdx * 0.05);
    const med = Math.floor(maxIdx * 0.5);
    const hi = Math.ceil(maxIdx * 0.95);

    const result = [
      {
        tier:
          (strategies[lo].risk_score?.total ?? Infinity) < LOW_RISK_MAX
            ? 'low'
            : 'medium',
        searchCandidate: strategies[lo],
      },
    ];

    if (med > lo) {
      result.push({
        tier:
          (strategies[med].risk_score?.total ?? Infinity) < LOW_RISK_MAX
            ? 'low'
            : 'medium',
        searchCandidate: strategies[med],
      });
    }

    if (hi > med) {
      result.push({
        tier:
          (strategies[hi].risk_score?.total ?? Infinity) < LOW_RISK_MAX
            ? 'low'
            : 'medium',
        searchCandidate: strategies[hi],
      });
    }

    return result as unknown as TradeCard[];
  }

  const tiered = pickByRiskTier(strategies);
  if (tiered.length > 0) {
    return tiered;
  }
  return [...strategies]
    .sort((a, b) => b.final_score! - a.final_score!)
    .slice(0, 3)
    .map((searchCandidate) => ({ tier: null, searchCandidate }));
}

/**
 * The trade structures to show for an analysis, given the trade filters and the trade
 * criteria's min reward-to-risk at the time. MoviefoneChat calls this once, right after
 * fetching an analysis, so the selection stays fixed in the conversation from then on.
 */
export function selectTradeStructures(
  candidates: SearchCandidate[],
  riskTolerance: MoviefoneRiskTolerance,
  min_pnl_ratio: number,
  max_pnl_ratio: number,
): SearchCandidate[] {
  return pickTradeCards(
    candidates,
    riskTolerance,
    min_pnl_ratio,
    max_pnl_ratio,
  ).map((c) => c.searchCandidate);
}

const legKey = (leg: StructureLeg) => [
  leg.action,
  leg.option_type,
  leg.strike,
  leg.expiry,
  leg.quantity,
];

/** Same structure and legs, whatever its current prices. */
export const isSameTrade = (a: SearchCandidate, b: SearchCandidate) =>
  a.family === b.family && isEqual(a.legs.map(legKey), b.legs.map(legKey));
